import { useCallback, useEffect, useMemo, useState } from 'react';
import { dataStagingAPI, featureFlagsAPI, studentsAPI } from '../services/api';
import {
  getCategoryPosition,
  getNextInCategory,
  primaryStagingReviewCategory,
  annotateQueueEmailDuplicates,
  isInFileDuplicateDoc,
} from '../utils/stagingReviewCategories';
import {
  mapCompletenessStudentToQueueItem,
  parseCompletenessStudentId,
} from '../domain/studentCompleteness/profileCompletenessFields';
import { resolveEnrollmentDecision } from '../utils/stagingStudentEditForm';
import {
  buildDecideOverridesFromAdminForm,
  resolveEnrollmentDecisionFromAdminForm,
} from '../utils/stagingAdminFormDecide';
import { validateAdminEditUserForm } from '../utils/validateAdminEditUserForm';
import { executeAdminUserFormUpdate } from '../utils/submitAdminUserFormUpdate';

export const STAGING_FLAG = 'data.staging.enabled';

/**
 * Review-queue + save: Edit User persistence (executeAdminUserFormUpdate) with staging
 * decide used to create new students and clear the review queue.
 */
export default function useStagingReview() {
  const [enabled, setEnabled] = useState(null);
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [selectedId, setSelectedId] = useState(null);
  const [loading, setLoading] = useState(false);
  const [acting, setActing] = useState(false);
  const [error, setError] = useState('');
  const [message, setMessage] = useState('');
  const [showJson, setShowJson] = useState(false);
  const [enrollmentDecision, setEnrollmentDecision] = useState('skip');
  const [completenessStats, setCompletenessStats] = useState(null);
  const [selectedFieldKey, setSelectedFieldKey] = useState(null);
  const [completenessItems, setCompletenessItems] = useState([]);
  const [completenessTotal, setCompletenessTotal] = useState(0);
  const [completenessLoading, setCompletenessLoading] = useState(false);
  const [duplicateCluster, setDuplicateCluster] = useState({
    loading: false,
    email: '',
    rows: [],
    documents: [],
  });

  const selected = useMemo(() => {
    if (!selectedId) return null;
    return (
      items.find((d) => d.id === selectedId) ||
      completenessItems.find((d) => d.id === selectedId) ||
      duplicateCluster.documents.find((d) => d.id === selectedId) ||
      null
    );
  }, [items, completenessItems, selectedId, duplicateCluster.documents]);

  const nextInCategory = useMemo(() => {
    if (selectedFieldKey) {
      const idx = completenessItems.findIndex((d) => d.id === selectedId);
      if (idx < 0 || idx >= completenessItems.length - 1) return null;
      return completenessItems[idx + 1];
    }
    return getNextInCategory(items, selectedId);
  }, [selectedFieldKey, completenessItems, selectedId, items]);

  const categoryPosition = useMemo(() => {
    if (selectedFieldKey) {
      const idx = completenessItems.findIndex((d) => d.id === selectedId);
      const field = (completenessStats?.byField || []).find((f) => f.key === selectedFieldKey);
      return {
        index: idx + 1,
        total: completenessItems.length,
        categoryId: selectedFieldKey,
        categoryLabel: field?.label || selectedFieldKey,
      };
    }
    return getCategoryPosition(items, selectedId);
  }, [selectedFieldKey, completenessItems, selectedId, completenessStats]);

  useEffect(() => {
    featureFlagsAPI
      .status([STAGING_FLAG])
      .then((res) => setEnabled(Boolean(res.data?.data?.enabled?.[STAGING_FLAG])))
      .catch(() => setEnabled(false));
  }, []);

  const loadQueue = useCallback(
    async ({ preferId, preferCategoryId } = {}) => {
      if (!enabled) return { list: [], nextId: null };
      setLoading(true);
      setError('');
      try {
        const res = await dataStagingAPI.reviewQueue({ take: 500 });
        const list = annotateQueueEmailDuplicates(res.data?.items || []);
        setItems(list);
        setTotal(res.data?.total || list.length);

        let nextId = null;
        if (preferId && list.some((d) => d.id === preferId)) {
          nextId = preferId;
        } else if (preferCategoryId) {
          const peer = list.find(
            (d) => primaryStagingReviewCategory(d) === preferCategoryId
          );
          nextId = peer?.id || null;
        } else if (list.length && selectedId && list.some((d) => d.id === selectedId)) {
          nextId = selectedId;
        } else if (list.length) {
          nextId = list[0].id;
        }

        setSelectedId(nextId);
        return { list, nextId };
      } catch (err) {
        if (err.response?.data?.code === 'FEATURE_DISABLED') {
          setEnabled(false);
        } else {
          setError(err.response?.data?.message || 'Failed to load review queue');
        }
        return { list: [], nextId: null };
      } finally {
        setLoading(false);
      }
    },
    [enabled, selectedId]
  );

  const loadCompletenessStats = useCallback(async () => {
    try {
      const res = await studentsAPI.completenessStats();
      setCompletenessStats(res.data || null);
    } catch {
      setCompletenessStats(null);
    }
  }, []);

  const loadCompletenessField = useCallback(async (fieldKey, { preferId } = {}) => {
    if (!fieldKey) {
      setCompletenessItems([]);
      setCompletenessTotal(0);
      return [];
    }
    setCompletenessLoading(true);
    try {
      const res = await studentsAPI.completenessList({
        missingField: fieldKey,
        status: 'all',
        page: 1,
        limit: 200,
      });
      const list = (res.data?.items || [])
        .map(mapCompletenessStudentToQueueItem)
        .filter(Boolean);
      setCompletenessItems(list);
      setCompletenessTotal(res.data?.total ?? list.length);
      if (preferId && list.some((d) => d.id === preferId)) {
        setSelectedId(preferId);
      } else if (list.length) {
        setSelectedId(list[0].id);
      } else {
        setSelectedId(null);
      }
      return list;
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to load students for this field');
      setCompletenessItems([]);
      setCompletenessTotal(0);
      return [];
    } finally {
      setCompletenessLoading(false);
    }
  }, []);

  const selectField = useCallback(
    (fieldKey) => {
      setSelectedFieldKey(fieldKey);
      if (!fieldKey) return;
      loadCompletenessField(fieldKey);
    },
    [loadCompletenessField]
  );

  useEffect(() => {
    if (enabled) {
      loadQueue();
      loadCompletenessStats();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enabled]);

  useEffect(() => {
    if (!selected) {
      setEnrollmentDecision('skip');
      return;
    }
    setShowJson(false);
    setEnrollmentDecision(resolveEnrollmentDecision(selected));
  }, [selected]);

  const toggleJson = useCallback(() => {
    setShowJson((v) => !v);
  }, []);

  const afterSaveNavigate = useCallback(
    async (baseMsg, { goNext, savedCategory, upcomingId }) => {
      await loadCompletenessStats();
      if (selectedFieldKey) {
        const list = await loadCompletenessField(selectedFieldKey, {
          preferId: goNext ? upcomingId : selectedId,
        });
        if (goNext && upcomingId && list.some((d) => d.id === upcomingId)) {
          setMessage(
            `${baseMsg}. Opened next student missing ${savedCategory || selectedFieldKey}.`
          );
        } else {
          setMessage(baseMsg);
        }
        return;
      }

      const { list, nextId } = await loadQueue({
        preferId: upcomingId,
        preferCategoryId: goNext || upcomingId ? savedCategory : null,
      });

      if (goNext && (upcomingId || nextId)) {
        setMessage(
          `${baseMsg}. Opened next student in ${savedCategory.replace(/_/g, ' ')}.`
        );
      } else if (
        goNext &&
        !list.some((d) => primaryStagingReviewCategory(d) === savedCategory)
      ) {
        setMessage(`${baseMsg}. No more students in this category.`);
      } else {
        setMessage(baseMsg);
      }
    },
    [loadQueue, loadCompletenessStats, loadCompletenessField, selectedFieldKey, selectedId]
  );

  const applyDocument = useCallback(
    async (
      formData,
      {
        goNext = false,
        userId = null,
        companyOptions,
        refreshCompanyOptions,
      } = {}
    ) => {
      if (!selected) return;
      const isCompleteness = Boolean(parseCompletenessStudentId(selected.id));
      const savedCategory = selectedFieldKey || primaryStagingReviewCategory(selected);
      const upcomingId = goNext ? nextInCategory?.id || null : null;

      const enrollment = resolveEnrollmentDecisionFromAdminForm(
        formData,
        enrollmentDecision
      );
      // Same required fields as Edit User; batch/course/mode only when enrolling via staging
      const validationError = validateAdminEditUserForm(formData, {
        requireEnrollment: enrollment !== 'skip',
        allowPartial: true,
      });
      if (validationError) {
        setError(validationError);
        return;
      }

      setActing(true);
      setError('');
      setMessage('');
      try {
        let resolvedUserId = userId;

        // New students: create via staging decide first (same identity/enrollment pipeline)
        if (!resolvedUserId && !isCompleteness) {
          const createRes = await dataStagingAPI.decide(selected.id, {
            action: 'apply',
            enrollmentDecision: enrollment,
            overrides: buildDecideOverridesFromAdminForm(formData),
          });
          if (!createRes.data?.applied) {
            throw new Error(
              createRes.data?.message ||
                createRes.data?.error ||
                'Staging create/apply failed'
            );
          }
          resolvedUserId = createRes.data?.outcome?.userId || null;
          if (!resolvedUserId) {
            await afterSaveNavigate(
              `Saved via staging (${createRes.data.status}) — open Edit User if you need more fields`,
              { goNext, savedCategory, upcomingId }
            );
            return;
          }
        }

        // Exact Edit User save path
        await executeAdminUserFormUpdate({
          userId: resolvedUserId,
          formData,
          companyOptions,
          refreshCompanyOptions,
        });

        if (!isCompleteness) {
          try {
            await dataStagingAPI.decide(selected.id, {
              action: 'apply',
              enrollmentDecision: enrollment,
              overrides: buildDecideOverridesFromAdminForm(formData),
            });
          } catch {
            // Row may already be applied after create — still success for Edit User save
          }
        }

        await afterSaveNavigate('Saved with Edit User update', {
          goNext,
          savedCategory,
          upcomingId,
        });
      } catch (err) {
        const details = Array.isArray(err.response?.data?.errors)
          ? err.response.data.errors.map((e) => `${e.field}: ${e.message}`).join('; ')
          : null;
        setError(
          details ||
            err.response?.data?.message ||
            err.response?.data?.error ||
            err.message ||
            'Save failed'
        );
      } finally {
        setActing(false);
      }
    },
    [selected, nextInCategory, enrollmentDecision, afterSaveNavigate, selectedFieldKey]
  );

  const skipDocument = useCallback(async () => {
    if (!selected) return;
    const isCompleteness = Boolean(parseCompletenessStudentId(selected.id));
    const savedCategory = selectedFieldKey || primaryStagingReviewCategory(selected);
    const upcomingId = nextInCategory?.id || null;

    if (isCompleteness) {
      if (upcomingId) setSelectedId(upcomingId);
      setMessage('Moved to next student in this field');
      return;
    }

    setActing(true);
    setError('');
    setMessage('');
    try {
      await dataStagingAPI.decide(selected.id, {
        action: 'skip',
        note: 'Skipped from review screen',
      });
      await afterSaveNavigate('Document skipped', {
        goNext: true,
        savedCategory,
        upcomingId,
      });
    } catch (err) {
      setError(
        err.response?.data?.message || err.response?.data?.error || 'Skip failed'
      );
    } finally {
      setActing(false);
    }
  }, [selected, nextInCategory, afterSaveNavigate, selectedFieldKey]);

  const goNextOnly = useCallback(() => {
    if (nextInCategory) {
      setSelectedId(nextInCategory.id);
      setMessage('');
      setError('');
    }
  }, [nextInCategory]);

  useEffect(() => {
    let cancelled = false;
    const run = async () => {
      if (!selected || !isInFileDuplicateDoc(selected) || String(selected.id).startsWith('completeness:')) {
        setDuplicateCluster({ loading: false, email: '', rows: [], documents: [] });
        return;
      }
      setDuplicateCluster((prev) => ({ ...prev, loading: true }));
      try {
        const res = await dataStagingAPI.duplicateCluster(selected.id);
        if (cancelled) return;
        const docs = res.data?.documents || [];
        const annotated = annotateQueueEmailDuplicates(docs);
        setDuplicateCluster({
          loading: false,
          email: res.data?.email || selected.emailKey || '',
          rows: res.data?.rows || [],
          documents: annotated,
        });
      } catch {
        if (cancelled) return;
        const email = String(selected.emailKey || selected.ruleResult?.email || '')
          .trim()
          .toLowerCase();
        const peers = (items || []).filter((d) => {
          const other = String(d.emailKey || d.ruleResult?.email || '')
            .trim()
            .toLowerCase();
          return email && other === email;
        });
        setDuplicateCluster({
          loading: false,
          email: selected.emailKey || selected.ruleResult?.email || '',
          rows: peers.map((d) => ({
            id: d.id,
            email: d.emailKey || '',
            externalKey: d.externalKey || String(d.id).slice(0, 8),
            status: d.status,
            firstName: d.ruleResult?.firstName || '',
            lastName: d.ruleResult?.lastName || '',
            phone: d.ruleResult?.studentPatch?.phoneNumber || '',
            batch: d.ruleResult?.enrollment?.batchId || '',
            course: '',
            filledFieldCount: null,
          })),
          documents: peers,
        });
      }
    };
    run();
    return () => {
      cancelled = true;
    };
  }, [selected, items]);

  const decideDuplicateCluster = useCallback(
    async (action, options = {}) => {
      if (!selected) return;
      const savedCategory = primaryStagingReviewCategory(selected);
      const upcomingId = nextInCategory?.id || null;
      setActing(true);
      setError('');
      setMessage('');
      try {
        const body = {
          action,
          enrollmentDecision:
            action === 'add_enrollment' ? 'create' : enrollmentDecision || 'skip',
        };
        if (Array.isArray(options.mergeFromIds)) {
          body.mergeFromIds = options.mergeFromIds;
        }
        if (options.batchId) body.batchId = options.batchId;
        const res = await dataStagingAPI.decideCluster(selected.id, body);
        if (!res.data?.success && !res.data?.applied) {
          throw new Error(res.data?.message || 'Duplicate-email action failed');
        }
        const skipped = Number(res.data?.skipped) || 0;
        const enrolled = Array.isArray(res.data?.enrolledBatchIds)
          ? res.data.enrolledBatchIds.length
          : 0;
        const label =
          action === 'merge'
            ? 'Merged duplicate rows'
            : action === 'add_enrollment'
              ? 'Added enrollment'
              : 'Accepted this record';
        const parts = [];
        if (enrolled) parts.push(`${enrolled} enrollment(s) kept`);
        const courseOnly = Array.isArray(res.data?.courseOnlyNames)
          ? res.data.courseOnlyNames.length
          : 0;
        if (courseOnly) parts.push(`${courseOnly} course(s) kept without enrollment`);
        if (skipped) parts.push(`${skipped} other row(s) skipped`);
        await afterSaveNavigate(parts.length ? `${label} (${parts.join(', ')})` : label, {
          goNext: true,
          savedCategory,
          upcomingId,
        });
      } catch (err) {
        setError(
          err.response?.data?.message ||
            err.response?.data?.error ||
            err.message ||
            'Duplicate-email action failed'
        );
      } finally {
        setActing(false);
      }
    },
    [selected, nextInCategory, enrollmentDecision, afterSaveNavigate]
  );

  const onImported = useCallback(async () => {
    setMessage('Import complete — refreshing review queue');
    await Promise.all([loadQueue(), loadCompletenessStats()]);
    if (selectedFieldKey) await loadCompletenessField(selectedFieldKey);
  }, [loadQueue, loadCompletenessStats, loadCompletenessField, selectedFieldKey]);

  const refreshAll = useCallback(async () => {
    await Promise.all([loadQueue(), loadCompletenessStats()]);
    if (selectedFieldKey) await loadCompletenessField(selectedFieldKey);
  }, [loadQueue, loadCompletenessStats, loadCompletenessField, selectedFieldKey]);

  return {
    enabled,
    items,
    total,
    selectedId,
    setSelectedId,
    selected,
    loading,
    acting,
    error,
    message,
    showJson,
    toggleJson,
    enrollmentDecision,
    setEnrollmentDecision,
    nextInCategory,
    categoryPosition,
    hasNext: Boolean(nextInCategory),
    loadQueue: refreshAll,
    applyDocument,
    skipDocument,
    decideDuplicateCluster,
    duplicateCluster,
    goNextOnly,
    onImported,
    completenessStats,
    completenessItems,
    completenessTotal,
    completenessLoading,
    selectedFieldKey,
    selectField,
  };
}
